import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import MuiLink from '@mui/material/Link';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import type { TAnalyticsMyRequirementBE } from '@onesource/schemas';
import { Link } from '@tanstack/react-router';
import DOMPurify from 'dompurify';
import type { FC } from 'react';
import { MdOpenInNew } from 'react-icons/md';

import { getRequirementDisplay, prettifyEvent } from './requirement-display';
import { getRequirementLink } from './requirement-link';

const formatDueDate = (isoDate: string): string =>
  new Date(`${isoDate}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

/** When the requirement was (last) done, in the member's locale — e.g. "Sep 22, 2026, 8:10 AM". */
const formatDoneAt = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

/** One requirement: status chip, name (linked to the content when it has a page) and what is being counted. */
export const RequirementRow: FC<{ requirement: TAnalyticsMyRequirementBE }> = ({ requirement }) => {
  const display = getRequirementDisplay(requirement);
  const details: string[] = [];
  if (requirement.kind === 'rule') {
    details.push(
      requirement.progress
        ? `Rule · ${requirement.progress.completed} of ${requirement.progress.total} completed`
        : 'Rule',
    );
  } else {
    if (requirement.event) {
      details.push(prettifyEvent(requirement.event));
    }
    details.push(
      requirement.target === 1
        ? `Done ${requirement.value} of 1`
        : `Done ${requirement.value} of ${requirement.target} times`,
    );
    if (requirement.dueDate) {
      details.push(`Due ${formatDueDate(requirement.dueDate)}`);
    }
    if (requirement.lastDoneAt) {
      details.push(`${requirement.met ? 'Completed' : 'Last done'} ${formatDoneAt(requirement.lastDoneAt)}`);
    }
  }

  const label = <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(requirement.label) }} />;
  const link = getRequirementLink(requirement);

  return (
    <ListItem
      disableGutters
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        py: 1.25,
        px: { xs: 1.5, md: 2 },
        borderBottom: theme => `1px solid ${theme.palette.divider}`,
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Chip
        icon={display.icon}
        label={display.label}
        color={display.color}
        variant={display.variant}
        size="small"
        sx={{ flexShrink: 0, minWidth: 108 }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontFamily: '"Figtree", sans-serif',
            fontWeight: 600,
            fontSize: 15,
            lineHeight: '20px',
            color: '#2e2e2d',
            overflowWrap: 'anywhere',
          }}
        >
          {link?.external ? (
            <MuiLink
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              {label}
              <MdOpenInNew size={14} aria-label="Opens in the app" />
            </MuiLink>
          ) : link ? (
            <Link to={link.href} style={{ color: 'inherit', textDecoration: 'underline' }}>
              {label}
            </Link>
          ) : (
            label
          )}
        </Typography>
        {details.length > 0 && (
          <Typography variant="body2" sx={{ color: theme => theme.palette.grey[600], fontSize: 13 }}>
            {details.join(' · ')}
          </Typography>
        )}
      </Box>
    </ListItem>
  );
};
